import type { Immutable } from "immer";
import React from "react";
import { OrderCancelButton } from "tsi-common-react/src/apps/customer-account/OrderCancelButton/index";
import { t } from "ttag";

import { templates } from "@reactivated";

import { CustomerServiceInfoBlock } from "./CustomerServiceInfoBlock";

type IDocProps = Immutable<templates.AnonOrderDocumentTemplate>;
type IOrderProps = IDocProps["order"];
type ISummaryProps = NonNullable<IOrderProps["summary"]>;
type ILineProps = IOrderProps["shipped_events"][number]["lines"][number];

const REGISTER_PRODUCT_URL = "https://register.tempurpedic.com/register/";

// A titled progress bar with an optional guide item. An empty title renders an
// empty div.progress-bar (no matching branch, so no title/guide).
const ProgressBar = (
    props: Immutable<{ bar: ILineProps["progress_bar"] }>,
): React.JSX.Element => {
    const { bar } = props;
    return (
        <div className="progress-bar">
            {bar.title && (
                <div className="progress-bar__list">
                    <h3 className="progress-bar__title">{bar.title}</h3>
                    {bar.item_modifier && (
                        <div className="progress-bar__guide">
                            <div
                                className={`progress-bar__item progress-bar__item--${bar.item_modifier}`}
                            ></div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Per-line status block: image, title, qty, price, reviews, progress bar, and
// tracking link.
const OrderLine = (
    props: Immutable<{ line: ILineProps }>,
): React.JSX.Element => {
    const { line } = props;
    return (
        <div className="order-detail">
            <div className="section__left products__results">
                <div className="product__image">
                    <a href={line.pdp_url}>
                        {line.image_url && (
                            <img
                                className="responsive-img"
                                src={line.image_url}
                                alt={line.image_alt}
                                loading="lazy"
                                decoding="async"
                            />
                        )}
                    </a>
                </div>
                <div className="product__details">
                    <div className="product__details__copy">
                        {line.title}
                        <br />
                        {t`Qty:`} {line.quantity}
                        <br />
                        {!line.is_bundled && line.price && (
                            <>
                                {line.price.is_discounted ? (
                                    <div className="product__price-container">
                                        <span className="product__actual-price">
                                            {line.price.actual_display}
                                        </span>
                                        {line.price.addon_display && (
                                            <>
                                                {" "}
                                                <span className="product__retail-price">
                                                    +{line.price.addon_display}
                                                </span>
                                            </>
                                        )}{" "}
                                        <span className="product__retail-price product__retail-price--strike">
                                            {line.price.msrp_display}
                                        </span>
                                    </div>
                                ) : (
                                    <>
                                        <div className="product__retail-price">
                                            {line.price.actual_display}
                                        </div>
                                        {line.price.addon_display && (
                                            <span className="product__retail-price">
                                                +{line.price.addon_display}
                                            </span>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                        {line.is_bundled && (
                            <div className="order-detail__bundle-callout">
                                {line.bundle_callout_text}
                            </div>
                        )}
                        <div className="product__reviews">
                            {line.reviews.has_write_review && (
                                <a
                                    href={line.reviews.write_review_url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {t`Write a Review`}
                                </a>
                            )}
                            {line.reviews.show_divider && (
                                <>
                                    {" "}
                                    <span className="product__copy-divider">
                                        &#124;
                                    </span>{" "}
                                </>
                            )}
                            {line.reviews.show_register_link && (
                                <a
                                    href={REGISTER_PRODUCT_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {t`Register Product`}
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="section__right product__status">
                <ProgressBar bar={line.progress_bar} />
                {line.show_submit_failed_copy && (
                    <div className="product__status__copy">
                        <p className="product__shipping-status">{t`Online tracking is unavailable for this order. Please contact us at 800-821-6621 if you have questions about your order.`}</p>
                    </div>
                )}
                <div className="product__status__copy">
                    {line.tracking.show_link &&
                        (line.tracking.is_freight ? (
                            <div className="product__shipping-status--tracking-link-container">
                                <a
                                    className="button product__shipping-status--wg-tracking product__shipping-status--tracking-link al-shipping--wg-tracking"
                                    href={line.tracking.tracking_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t`Track Shipment`}
                                </a>
                            </div>
                        ) : (
                            <div className="product__shipping-status--tracking-link-container">
                                <a
                                    className="button product__shipping-status--tracking-link"
                                    href={line.tracking.tracking_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t`Track Shipment`}
                                </a>
                            </div>
                        ))}
                </div>
            </div>
        </div>
    );
};

// The order summary header + address + payment + totals.
const OrderSummary = (
    props: Immutable<{ summary: ISummaryProps }>,
): React.JSX.Element => {
    const { summary } = props;
    const addr = summary.shipping_address;
    return (
        <div className="order-summary-details details">
            <div className="order-summary-details__header">
                <div className="order-summary-details__info">
                    <h3 className="order-summary-details__order-date">
                        {summary.date_placed_display}
                    </h3>
                    <div className="order-summary-details__info-column order-summary-details__info-column--order-number">
                        {t`Order Number:`} {summary.order_number}
                    </div>
                    <div className="order-summary-details__info-column order-summary-details__info-column--total-price-wrapper">
                        {t`Total:`}{" "}
                        <span className="order-summary-details__info-column--total-price">
                            {summary.order_total_display}
                        </span>
                    </div>
                </div>
                <div className="order-summary-details__info">
                    <div className="order-summary-details__info-column order-summary-details__info-column--order-actions">
                        {summary.cancel_button && (
                            <OrderCancelButton {...summary.cancel_button} />
                        )}
                    </div>
                </div>
                <button className="order-summary-details__info-column order-summary-details__info-column--order-details">
                    <span className="order-summary-details__order-details">
                        {t`Order Details`}
                    </span>
                </button>
            </div>

            <div className="order-summary-details__columns">
                <div className="order-summary-details__column">
                    <h4 className="order-summary-details__column__title">
                        {t`Shipping Address`}
                    </h4>
                    <div className="order-summary-details__copy-block order-summary-details__address">
                        <div>{addr.name}</div>
                        <div>
                            {addr.line1}
                            {addr.line2 && <>, {addr.line2}</>}
                        </div>
                        <div>
                            {addr.city}, {addr.state} {addr.postcode}
                            <br />
                            {addr.phone_number}
                        </div>
                    </div>
                </div>

                <div className="order-summary-details__column">
                    <h4 className="order-summary-details__column__title">
                        {t`Payment Method`}
                    </h4>
                    <div className="order-summary-details__copy-block order-summary-details__payment">
                        {summary.cybersource_cards.map((card, i) => (
                            <div
                                key={`cs-${i}`}
                                className="order-summary-details__cc_payment"
                            >
                                <span className="order-summary-details__card_type">
                                    {card.card_type_name}
                                </span>{" "}
                                <span className="order-summary-details__card_mask">
                                    {t`ending in`} {card.last4}
                                </span>
                                {card.amount_display && (
                                    <span className="order-summary-details__amount">
                                        {card.amount_display}
                                    </span>
                                )}
                            </div>
                        ))}
                        {summary.financing_transfers.map((transfer, i) => (
                            <div
                                key={`fin-${i}`}
                                className="order-summary-details__financing_payment"
                            >
                                <div className="order-summary-details__account">
                                    <span>
                                        {t`Account ending in`}{" "}
                                        {transfer.masked_account_number}
                                    </span>
                                    {transfer.amount_display && (
                                        <span className="order-summary-details__amount">
                                            {transfer.amount_display}
                                        </span>
                                    )}
                                </div>
                                {transfer.disclosure && (
                                    <span className="order-summary-details__terms">
                                        {t`Terms:`} {transfer.disclosure}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="order-summary-details__breakdown order-summary-details__column">
                    <h4 className="order-summary-details__column__title">
                        {t`Order Summary`}
                    </h4>
                    <div className="order-summary-details__copy-block">
                        <div className="order-summary-details__breakdown-row">
                            <div className="order-summary-details__breakdown-title">
                                {t`Subtotal:`}
                            </div>
                            <div className="order-summary-details__breakdown-number">
                                {summary.subtotal_display}
                            </div>
                        </div>

                        <div className="order-summary-details__breakdown-row">
                            <div className="order-summary-details__breakdown-title">
                                {t`Shipping:`}
                            </div>
                            <div className="order-summary-details__breakdown-number">
                                {summary.shipping_display}
                            </div>
                        </div>

                        {summary.is_tax_known ? (
                            summary.itemized_tax_groups.map((group, i) => (
                                <div
                                    key={i}
                                    className="order-summary-details__breakdown-row"
                                >
                                    <div className="order-summary-details__breakdown-title">
                                        {group.name}
                                    </div>
                                    <div className="order-summary-details__breakdown-number">
                                        {group.amount_display}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="order-summary-details__breakdown-row">
                                <div className="order-summary-details__breakdown-title">
                                    {t`Tax:`}
                                </div>
                                <div className="order-summary-details__breakdown-number">
                                    {t`Tax will be calculated upon shipment`}
                                </div>
                            </div>
                        )}

                        <div className="order-summary-details__breakdown-row">
                            {summary.has_discount && (
                                <>
                                    <div className="order-summary-details__breakdown-title">
                                        {t`Discount:`}
                                    </div>
                                    <div className="order-summary-details__breakdown-number order-summary-details__breakdown-number--cerise">
                                        {summary.discount_display}
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="order-summary-details__breakdown-row order-summary-details__breakdown-row--total">
                            <div className="order-summary-details__breakdown-title">
                                {t`Order Total:`}
                            </div>
                            <div className="order-summary-details__breakdown-number">
                                {summary.order_total_display}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// The order detail: summary + shipped-event sections + unshipped section.
const OrderDetailPartial = (
    props: Immutable<{ order: IOrderProps }>,
): React.JSX.Element => {
    const { order } = props;
    return (
        <div className="order-summary">
            {order.summary && <OrderSummary summary={order.summary} />}

            {order.shipped_events.map((event, i) => (
                <div
                    key={`ship-${i}`}
                    className="order-summary__body order-summary__body--shipping-events-"
                >
                    <div className="section products">
                        {event.lines.map((line, j) => (
                            <OrderLine key={j} line={line} />
                        ))}
                    </div>
                </div>
            ))}

            {order.unshipped && (
                <div className="order-summary__body order-summary__body--unshipped-order">
                    <div className="section products">
                        {order.unshipped.lines.map((line, i) => (
                            <OrderLine key={i} line={line} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// The full anonymous order-status section: header + order detail OR empty state
// + customer-service block.
export const AnonOrder = (props: IDocProps): React.JSX.Element => {
    const { order, customer_service } = props;
    return (
        <section
            id="order-detail"
            className="l-capped-width u-margin-bottom--xl u-full-width-title order-list"
        >
            <h2>
                {t`Order:`} {order.order_number}
            </h2>

            <div
                className={`content__copy--full-width content__orders-page${
                    order.has_order
                        ? ""
                        : " content__orders-page--empty-order-status"
                }`}
            >
                <div className="orders">
                    <ul className="orders__list">
                        {order.has_order ? (
                            <li className="orders__list-item">
                                <div className="content__copy--full-width order-summary-wrapper">
                                    <OrderDetailPartial order={order} />
                                </div>
                            </li>
                        ) : order.form_is_bound ? (
                            <li className="orders__list-item">
                                {t`No orders match your search.`}
                            </li>
                        ) : (
                            <li className="orders__list-item orders__list-item--empty-order-status">
                                <h2 className="orders__list-item__title orders__list-item__title--empty-order-status">
                                    {t`You haven't shopped with us.`}
                                </h2>
                                <p className="orders__list-item__copy orders__list-item__copy--empty-order-status">
                                    {t`Please check back after your first purchase to view your order details.`}
                                </p>
                                <a className="button" href="/">
                                    {t`Shop now`}
                                </a>
                            </li>
                        )}
                    </ul>
                </div>
            </div>

            <CustomerServiceInfoBlock {...customer_service} />
        </section>
    );
};
